import { CardNumberElement } from "@stripe/react-stripe-js";
import { FaInfoCircle } from "react-icons/fa";

// "Create Your API Profile" section. Billing address lives in
// billingAddressFields below, under the "Billing Information" heading.
export const inputWithoutLogin = [
  {
    type: "text",
    placeholder: "Enter first name",
    name: "firstName",
    label: "First Name",
  },
  {
    type: "text",
    placeholder: "Enter last name",
    name: "lastName",
    label: "Last Name",
  },
  {
    type: "email",
    placeholder: "Enter email address",
    name: "email",
    label: "Email Address",
  },
  {
    type: "password",
    name: "password",
    label: "Password",
    placeholder: "Password at least 6 characters",
  },
  {
    type: "password",
    name: "confirmPassword",
    label: "Confirm Password",
    placeholder: "Password again please",
  },
  {
    type: "text",
    placeholder: "Create an Account ID",
    name: "userName",
    label: "Account ID",
  },
  {
    type: "tel",
    autocomplete: "tel",
    name: "phone",
    placeholder: "Enter phone number",
    label: "Phone Number",
  },
  {
    type: "date",
    placeholder: "Date of birth",
    name: "dateOfBirth",
    label: "Date of Birth",
  },
  {
    type: "text",
    name: "companyName",
    placeholder: "Enter company name or Self",
    label: "Company",
  },
];
export const inputWhenLogin = [
  {
    type: "text",
    placeholder: "First Name",
    name: "firstName",
    label: "First Name",
  },
  {
    type: "text",
    placeholder: "Last Name",
    name: "lastName",
    label: "Last Name",
  },
  {
    type: "tel",
    autocomplete: "tel",
    name: "phone",
    placeholder: "Enter phone number",
    label: "Phone Number",
  },
  {
    type: "text",
    name: "companyName",
    placeholder: "Enter company name or Self",
    label: "Company",
  },
];

// "Billing Information" section — rendered with the card fields, so it only
// appears when a new card is being entered. `md`/`colClass` put State and ZIP
// on one row with a gutter between them.
export const billingAddressFields = [
  {
    type: "text",
    name: "line1",
    placeholder: "Street address",
    label: "Billing Address",
    md: "12",
  },
  { type: "text", name: "city", placeholder: "City", label: "City", md: "12" },
  {
    type: "select",
    name: "state",
    placeholder: "Select state",
    label: "State",
    md: "6",
    // gutter only once the pair is side by side (md+); flush when stacked
    colClass: "p-0 pr-md-2",
  },
  {
    type: "text",
    name: "postalCode",
    placeholder: "ZIP code",
    label: "ZIP Code",
    md: "6",
    colClass: "p-0 pl-md-2",
  },
];
// Tokenize the card and attach the billing address for AVS + Stripe Tax.
// With split Elements, createToken(CardNumberElement) automatically uses the
// linked expiry/CVC fields from the same <Elements> group. US-only for now.
export const getStripeToken = async (elements, stripe, billing = {}) => {
  const cardElement = elements.getElement(CardNumberElement);

  const result = await stripe.createToken(cardElement, {
    ...(billing.name ? { name: billing.name } : {}),
    address_line1: billing.line1,
    address_city: billing.city,
    address_state: billing.state,
    address_zip: billing.postalCode,
    address_country: "US",
  });

  if (result.error) {
    alert(result.error.message);
    return;
  }
  return result.token.id;
};

/*
    THE PLAN CATALOGUE — one entry per plan, driving both the public pricing
    page and the account pages that name a customer's plan.

    Two field groups, and they are not interchangeable:

      * `features` is the bulleted "Includes:" list on the pricing card. It is
        marketing copy, ordered for reading.
      * `callPerMon`, `additionalApi`, `newCourse`, `submit`, `report` and
        friends are read individually by the account pages (MyApiPlan). Leave
        them in place even where `features` repeats them.

    QUOTAS ARE NOT FREE TEXT. The numbers below must agree with the usage plans
    in utils/apiUsagePlans.js, which mirrors what AWS API Gateway actually
    enforces. That file already warns that this one drifts — the All Courses
    entry advertised 100,000/month while the live plan is 3,572/day. Where the
    two are stated together it is because they describe the same allowance from
    different angles; do not change one without the other.

    `listed: false` keeps a plan off the pricing grid while still letting the
    account pages describe it.
*/
/*
    STARTER'S PRICE, IN DOLLARS. $49/month, set by Dave 2026-10-15
    (golfbert-api#34); limits are the AWS usage plan's (1,000 calls/month).
    The card and the checkout page (views/api/SubscribeStarter.js) both read
    this, so a new price is this one line. It must match the Stripe price
    `api-starter`.
*/
export const STARTER_MONTHLY_PRICE = 49;

export const planCardData = [
  {
    /*
        The free sample plan. Its AWS usage plan is "Trial" (ebww69), which
        enforces 1,000 calls per WEEK — not per month. The Settings page used
        to tell these customers 5,000 per month, a figure that belongs to the
        separate "Dev plan" usage plan.
    */
    id: "api-test",
    // Checkout identifies this plan as "test", not "api-test" — see
    // createStripePayload in components/global/Subscribe.js.
    priceId: "test",
    theme: "light",
    pill: null,
    nickname: "Sample Course",
    // Course 4803 is the one the trial actually entitles you to — the same id
    // GET /api-access reports as `courseSelection.fixedCourseIds`.
    subTitle:
      "Access to our sample course only – Riverbend Golf Complex (id 4803)",
    currency: "usd",
    unit_amount: 0,
    recurring: {
      interval: "month",
      trial_period_days: null,
    },
    btnText: "Try it out",
    path: "/api/subscribe/test",
    features: [
      "Access to our sample course only",
      "1,000 calls per week",
      "Rate limit: 1 request per second",
      "Burst quota: 10 concurrent connections",
    ],
    /*
        On a phone this card's list is the least useful of the three — it is
        the free tier, and the list repeats the description above it. Hidden
        by CSS at phone widths only; see .plan-tier--compact in plans.scss.
    */
    hideFeaturesOnMobile: true,
    // Read by the account pages, which describe a plan field by field.
    callPerMon: "1,000 calls per week",
    /*
        No `additionalApi` on purpose. The trial has no overage — exceeding
        the weekly allowance is refused, not billed — so quoting a per-call
        rate here would promise a service this plan does not offer. It also
        contradicted the Settings page, which tells these customers "Paid
        plans allow additional calls".
    */
    accessCourse: "Access to our sample course only",
    description: "",
    active: true,
  },
  {
    id: "api-single",
    theme: "light",
    pill: null,
    nickname: "Single Golf Course",
    subTitle: "Access to all data for any one golf course in the database",
    currency: "usd",
    unit_amount: 900 / 100,
    recurring: {
      interval: "month",
      trial_period_days: null,
    },
    btnText: "Purchase Plan",
    path: "/api/subscribe/single",
    features: [
      "Access to any one course",
      "10,000 calls per month",
      "Rate limit: 1 request per second",
      "Burst quota: 50 concurrent connections",
      "Submit updates",
      "Report errors",
    ],
    callPerMon: "10,000 calls per month",
    additionalApi: "Additional calls $0.005 per call",
    accessCourse: "Access to any one course",
    support: "Included Support",
    submit: "Submit updates",
    report: "Report errors",
    description: "",
    active: true,
  },
  {
    /*
        Every course on a small quota, for startup apps (golfbert-api#34;
        card copy from Dave, 2026-10-13). Its AWS usage plan is "Starter" (yq7p7h): 1,000
        calls per month, 1 per second, burst 50, and no overage — past the
        quota calls are refused, not billed.

        Price: STARTER_MONTHLY_PRICE, above.
    */
    id: "api-starter",
    theme: "light",
    pill: "Great for Startup Apps",
    nickname: "All Golf Courses – Starter",
    subTitle:
      "Access to all data for all golf courses in the database, limited call limits",
    currency: "usd",
    unit_amount: STARTER_MONTHLY_PRICE,
    recurring: {
      interval: "month",
      trial_period_days: null,
    },
    btnText: "Purchase Plan",
    path: "/api/subscribe/starter",
    features: [
      "Access to all golf courses",
      "1,000 calls per month",
      "Rate limit: 1 request per second",
      "Burst quota: 50 concurrent connections",
      { text: "New course requests", emphasis: true },
      "Submit updates",
      "Report errors",
    ],
    callPerMon: "1,000 calls per month",
    accessCourse: "Access to all golf courses",
    support: "Included Support",
    newCourse: "New course requests",
    submit: "Submit updates",
    report: "Report errors",
    description: "",
    active: true,
  },
  {
    id: "api-all",
    theme: "dark",
    pill: "Great for App Developers",
    nickname: "All Golf Courses – High Volume",
    subTitle: "Access to all data for all golf courses in the database",
    currency: "usd",
    unit_amount: 39900 / 100,
    recurring: {
      interval: "month",
      trial_period_days: null,
    },
    btnText: "Purchase Plan",
    path: "/api/subscribe/all",
    features: [
      "Access to all golf courses",
      // Both figures on purpose: 3,572/day is what AWS enforces (71z3ep) and
      // is the binding constraint; ~100,000/month is what that adds up to.
      "100,000 calls per month (3,572 daily limit)",
      "Additional calls $0.005 per call",
      "Rate limit: 3 requests per second",
      "Burst quota: 100 concurrent connections",
      /*
          A feature may be an object instead of a string when it needs to be
          picked out of the list. `emphasis` is the only flag FeatureList
          understands, and it means "this is the differentiator" — not a
          colour, so the light and dark cards can render it appropriately.
      */
      { text: "New course requests", emphasis: true },
      "Submit updates",
      "Report errors",
    ],
    callPerMon: "100,000 calls per month",
    additionalApi: "Daily limits apply. Additional calls $0.005 per call",
    accessCourse: "Access to all golf courses",
    support: "Included Support",
    newCourse: "New course requests",
    submit: "Submit updates",
    report: "Report errors",
    description:
      "Included with this plan is access to data for all golf courses in the database. Our staff will continue to add courses and manage the course data.",
    active: true,
  },
  {
    /*
        Not a card on the grid — it is a one-time purchase, not a monthly API
        plan, so it gets its own panel below the three tiers.
    */
    id: "static-data",
    listed: false,
    theme: "light",
    pill: "Great for Offline Applications",
    nickname: "Static Data Purchase",
    subTitle:
      "Get a snapshot of the entire database for use without monthly API fees. This is a great way to have access to the course data without having to rely on connectivity.",
    unit_amount: "120,000",
    recurring: {
      interval: "one-time fee",
      trial_period_days: null,
    },
    btnText: "Get in Touch",
    path: "/api/support",
    meta: "Based on purchase of complete dataset for all golf courses in the database. Data can be customized and price reflected to fit your business needs",
    features: [
      "No API calls needed",
      "You host and embed the data directly from your own servers",
      "You receive a snapshot of the data in its entirety",
      "Annual updates available with optional paid maintenance fees",
      "Submit course updates (included for existing courses)",
      "Report errors (included for existing courses)",
    ],
    callPerMon: "No API calls needed",
    additionalApi: "You own/host/embed the data",
    accessCourse: "Access to all courses",
    support: "Included Support",
    newCourse: "New course requests",
    submit: "Submit course updates",
    report: "Report errors",
    maintenanceFees:
      "Annual updates available with optional paid maintenance fees",
    description:
      "You will receive a copy of the current database in its entirety.",
    active: true,
  },
];
